import * as types from './actionTypes';
import { filter } from 'lodash';
import { getStoredPreference } from '../../utils/preferenceUtil';

// Default heatmap fill opacity per layer, expressed here as opacity (what
// Mapbox and this state actually store) - Observations defaults to 35%
// transparency (opacity 0.65), Tracks to 60% transparency (opacity 0.4).
// Single place both the initial-state hydration and the read selectors
// below pull from, so the two can never drift apart.
const DEFAULT_OBSERVATION_HEATMAP_OPACITY = 0.65;
const DEFAULT_TRACK_HEATMAP_OPACITY = 0.4;

const reducer = (
  state = {
    ontology: [],
    conceptCountById: {},
    languages: [],
    alertTimelineOpen: false,
    labels: [],
    showAgentNames: true,
    hideOldAgents: true,
    showInactiveAgents: false,
    showObservationClusters: false,
    observationClusterThreshold: 500,
    showObservationHeatmap: false,
    showTrackHeatmap: true,
    showTrackFullStandard: false,
    showTrackFullColour: false,
    observationHeatmapResolution: 25,
    trackHeatmapResolution: 25,
    observationHeatmapOpacity: getStoredPreference(
      'observationHeatmapOpacity',
      DEFAULT_OBSERVATION_HEATMAP_OPACITY
    ),
    trackHeatmapOpacity: getStoredPreference(
      'trackHeatmapOpacity',
      DEFAULT_TRACK_HEATMAP_OPACITY
    ),
    trackColor: 'yellow',
    showGrid: false,
    showCoordinates: false
  },
  action
) => {
  switch (action.type) {
    case types.SET_ONTOLOGY_SUCCESS: {
      const allNodes = action.payload.ontology.concepts;
      let topNodes = filter(allNodes || [], nodes => nodes.top);
      const treeData = buildOntologyTree(topNodes, allNodes || []);
      const mergedTree = mergeOntologyNodes(state.ontology, treeData);
      const countByIdMap = new Map(
        Object.entries(state.conceptCountById || {}).map(([key, value]) => [
          String(key),
          Number(value) || 0
        ])
      );
      const mergedTreeWithCounts = addCountsToNodes(mergedTree, countByIdMap);
      return {
        ...state,
        ontology: mergedTreeWithCounts
      };
    }
    case types.SET_LABELS_SUCCESS: {
      return {
        ...state,
        labels: action.payload.labels.translation
      };
    }
    case types.SET_CONCEPTS_COUNT: {
      const { distinctValues, hasRecognizedShape } = extractDistinctValues(
        action.payload
      );
      if (!hasRecognizedShape) {
        return state;
      }
      const countById = new Map();
      (distinctValues || []).forEach(value => {
        const rawId =
          value && value._value !== undefined
            ? value._value
            : value && value.value !== undefined
            ? value.value
            : value && value.id !== undefined
            ? value.id
            : value && value.key !== undefined
            ? value.key
            : null;
        const rawCount =
          value && value.frequency !== undefined
            ? value.frequency
            : value && value.count !== undefined
            ? value.count
            : value && value._count !== undefined
            ? value._count
            : 0;
        const resolvedId =
          rawId && typeof rawId === 'object' && rawId.id !== undefined
            ? rawId.id
            : rawId;
        const resolvedCount = Number(rawCount) || 0;
        if (resolvedId !== null && resolvedId !== undefined) {
          countById.set(String(resolvedId), resolvedCount);
        }
      });
      const ontologyClone = addCountsToNodes(state.ontology, countById);
      const conceptCountById = {};
      countById.forEach((count, id) => {
        conceptCountById[String(id)] = Number(count) || 0;
      });

      return {
        ...state,
        ontology: ontologyClone,
        conceptCountById
      };
    }
    case types.SET_LANGUAGES:
      return {
        ...state,
        languages: action.payload
      };
    case types.SET_SELECTED_LANGUAGE:
      return {
        ...state,
        selectedLanguage: action.payload
      };
    case types.SET_UPDATE_INTERVAL: {
      return {
        ...state,
        updateInterval: action.payload
      };
    }
    case types.SET_ALERT_TIMELINE:
      return {
        ...state,
        alertTimelineOpen: action.payload
      };
    case types.SET_SHOW_AGENT_NAMES:
      return {
        ...state,
        showAgentNames: action.payload
      };
    case types.SET_HIDE_OLD_AGENTS:
      return {
        ...state,
        hideOldAgents: action.payload
      };
    case types.SET_SHOW_INACTIVE_AGENTS:
      return {
        ...state,
        showInactiveAgents: action.payload
      };
    case types.SET_SHOW_OBSERVATION_CLUSTERS:
      return {
        ...state,
        showObservationClusters: action.payload
      };
    case types.SET_OBSERVATION_CLUSTER_THRESHOLD:
      return {
        ...state,
        observationClusterThreshold: action.payload
      };
    case types.SET_SHOW_OBSERVATION_HEATMAP:
      return {
        ...state,
        showObservationHeatmap: action.payload
      };
    case types.SET_SHOW_TRACK_HEATMAP:
      return {
        ...state,
        showTrackHeatmap: action.payload
      };
    case types.SET_SHOW_TRACK_FULL_STANDARD:
      return {
        ...state,
        showTrackFullStandard: action.payload
      };
    case types.SET_SHOW_TRACK_FULL_COLOUR:
      return {
        ...state,
        showTrackFullColour: action.payload
      };
    case types.SET_OBSERVATION_HEATMAP_RESOLUTION:
      return {
        ...state,
        observationHeatmapResolution: action.payload
      };
    case types.SET_TRACK_HEATMAP_RESOLUTION:
      return {
        ...state,
        trackHeatmapResolution: action.payload
      };
    case types.SET_OBSERVATION_HEATMAP_OPACITY:
      return {
        ...state,
        observationHeatmapOpacity: action.payload
      };
    case types.SET_TRACK_HEATMAP_OPACITY:
      return {
        ...state,
        trackHeatmapOpacity: action.payload
      };
    case types.SET_TRACK_COLOR:
      return {
        ...state,
        trackColor: action.payload
      };
    case types.SET_SHOW_GRID:
      return {
        ...state,
        showGrid: action.payload
      };
    case types.SET_SHOW_COORDINATES:
      return {
        ...state,
        showCoordinates: action.payload
      };
    default:
      return state;
  }
};

const extractDistinctValues = payload => {
  if (!payload) return { distinctValues: [], hasRecognizedShape: false };
  if (
    payload['values-response'] &&
    Array.isArray(payload['values-response']['distinct-value'])
  ) {
    return {
      distinctValues: payload['values-response']['distinct-value'],
      hasRecognizedShape: true
    };
  }
  if (
    payload.valuesResponse &&
    Array.isArray(payload.valuesResponse.distinctValue)
  ) {
    return {
      distinctValues: payload.valuesResponse.distinctValue,
      hasRecognizedShape: true
    };
  }
  if (payload.values && Array.isArray(payload.values)) {
    return { distinctValues: payload.values, hasRecognizedShape: true };
  }

  const nestedCandidates = [payload.response, payload.data, payload.payload];
  for (const candidate of nestedCandidates) {
    if (
      candidate &&
      candidate['values-response'] &&
      Array.isArray(candidate['values-response']['distinct-value'])
    ) {
      return {
        distinctValues: candidate['values-response']['distinct-value'],
        hasRecognizedShape: true
      };
    }
    if (
      candidate &&
      candidate.valuesResponse &&
      Array.isArray(candidate.valuesResponse.distinctValue)
    ) {
      return {
        distinctValues: candidate.valuesResponse.distinctValue,
        hasRecognizedShape: true
      };
    }
    if (candidate && candidate.values && Array.isArray(candidate.values)) {
      return { distinctValues: candidate.values, hasRecognizedShape: true };
    }
  }

  return { distinctValues: [], hasRecognizedShape: false };
};

const buildOntologyTree = (topNodes, allNodes) => {
  const nodeList = Array.isArray(allNodes)
    ? allNodes
    : Object.values(allNodes || {});
  const nodesById = new Map(nodeList.map(node => [String(node.id), node]));
  const nodesByIndex = new Map(
    nodeList.map((node, index) => [String(index), node])
  );
  const visited = new Set();

  const asIndexKey = value => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return /^\d+$/.test(normalized) ? normalized : null;
  };

  const resolveChildNode = childRef => {
    if (childRef === null || childRef === undefined) return null;
    if (typeof childRef === 'object') {
      if (childRef.id !== undefined && nodesById.has(String(childRef.id))) {
        return nodesById.get(String(childRef.id));
      }
      return childRef;
    }
    const indexKey = asIndexKey(childRef);
    if (indexKey !== null && nodesByIndex.has(indexKey)) {
      return nodesByIndex.get(indexKey);
    }
    if (nodesById.has(String(childRef))) {
      return nodesById.get(String(childRef));
    }
    return null;
  };

  const buildNode = (node, level = 0) => {
    if (!node || level > 10) return null;
    const nodeId = String(node.id);
    if (visited.has(nodeId)) return null;
    visited.add(nodeId);

    const rawChildren = Array.isArray(node.children)
      ? node.children
      : node.children
      ? [node.children]
      : [];

    const children = rawChildren
      .map(resolveChildNode)
      .map(child => buildNode(child, level + 1))
      .filter(Boolean);

    const nextNode = { ...node };
    if (children.length) {
      nextNode.children = children;
    } else {
      delete nextNode.children;
    }

    return nextNode;
  };

  const topNodeList = (topNodes || []).filter(Boolean);
  if (topNodeList.length > 0) {
    return topNodeList.map(node => buildNode(node)).filter(Boolean);
  }

  // Fallback for ontologies without explicit `top=true`: derive roots by excluding all referenced children.
  const referencedIds = new Set();
  nodeList.forEach(node => {
    const rawChildren = Array.isArray(node.children)
      ? node.children
      : node.children
      ? [node.children]
      : [];
    rawChildren.forEach(childRef => {
      const resolved = resolveChildNode(childRef);
      if (resolved && resolved.id !== undefined) {
        referencedIds.add(String(resolved.id));
      }
    });
  });
  const derivedRoots = nodeList.filter(
    node => node && node.id !== undefined && !referencedIds.has(String(node.id))
  );

  const rootsToUse = derivedRoots.length > 0 ? derivedRoots : nodeList;
  return rootsToUse.map(node => buildNode(node)).filter(Boolean);
};

const addCountsToNodes = (nodes, countById) => {
  const sourceNodes = Array.isArray(nodes) ? nodes : [];
  let changed = false;
  const updatedNodes = sourceNodes.map(node => {
    const nodeId = String(node.id);
    const nextCount = countById.has(nodeId) ? countById.get(nodeId) : undefined;
    const hasCurrentCount = Object.prototype.hasOwnProperty.call(node, 'count');
    const currentCount = hasCurrentCount ? Number(node.count) || 0 : undefined;
    const countChanged =
      (nextCount === undefined && hasCurrentCount) ||
      (nextCount !== undefined && currentCount !== nextCount);

    const currentChildren = Array.isArray(node.children) ? node.children : [];
    const nextChildren = addCountsToNodes(currentChildren, countById);
    const childrenChanged = nextChildren !== currentChildren;

    if (!countChanged && !childrenChanged) {
      return node;
    }

    changed = true;
    const nextNode = { ...node };
    if (nextCount !== undefined) {
      nextNode.count = nextCount;
    } else {
      delete nextNode.count;
    }
    if (Array.isArray(node.children)) {
      nextNode.children = nextChildren;
    }
    return nextNode;
  });

  return changed ? updatedNodes : sourceNodes;
};

const mergeOntologyNodes = (previousNodes, nextNodes) => {
  const prevList = Array.isArray(previousNodes) ? previousNodes : [];
  const nextList = Array.isArray(nextNodes) ? nextNodes : [];
  const prevById = new Map(prevList.map(node => [String(node.id), node]));

  let changed = prevList.length !== nextList.length;
  const merged = nextList.map((nextNode, index) => {
    const prevNode = prevById.get(String(nextNode.id));
    if (!prevNode) {
      changed = true;
      return nextNode;
    }
    const mergedNode = mergeOntologyNode(prevNode, nextNode);
    if (!changed) {
      if (mergedNode !== prevNode || prevList[index] !== prevNode) {
        changed = true;
      }
    }
    return mergedNode;
  });

  return changed ? merged : prevList;
};

const mergeOntologyNode = (previousNode, nextNode) => {
  const prevChildren = Array.isArray(previousNode.children)
    ? previousNode.children
    : [];
  const nextChildren = Array.isArray(nextNode.children)
    ? nextNode.children
    : [];
  const mergedChildren = mergeOntologyNodes(prevChildren, nextChildren);

  const previousCount = previousNode.count;
  const nextCount =
    nextNode.count === undefined ? previousCount : Number(nextNode.count) || 0;

  const mergedNode = {
    ...previousNode,
    ...nextNode
  };

  if (nextCount !== undefined) {
    mergedNode.count = nextCount;
  } else {
    delete mergedNode.count;
  }

  if (nextChildren.length > 0) {
    mergedNode.children = mergedChildren;
  } else {
    delete mergedNode.children;
  }

  const sameCount =
    (previousCount === undefined && mergedNode.count === undefined) ||
    (Number(previousCount) || 0) === (Number(mergedNode.count) || 0);
  const sameChildren =
    (previousNode.children === undefined &&
      mergedNode.children === undefined) ||
    previousNode.children === mergedNode.children;
  const sameMeta = shallowEqualNodeMeta(previousNode, mergedNode);

  if (sameCount && sameChildren && sameMeta) {
    return previousNode;
  }

  return mergedNode;
};

const shallowEqualNodeMeta = (leftNode, rightNode) => {
  const leftKeys = Object.keys(leftNode).filter(
    key => key !== 'children' && key !== 'count'
  );
  const rightKeys = Object.keys(rightNode).filter(
    key => key !== 'children' && key !== 'count'
  );
  if (leftKeys.length !== rightKeys.length) return false;
  for (let i = 0; i < leftKeys.length; i += 1) {
    const key = leftKeys[i];
    if (!Object.prototype.hasOwnProperty.call(rightNode, key)) return false;
    if (leftNode[key] !== rightNode[key]) return false;
  }
  return true;
};

const ontology = state => state.ontology;
const labels = state => state.labels || [];
const languages = state => state.languages || [];
const selectedLanguage = state => state.selectedLanguage || '';
const updateInterval = state => state.updateInterval || 300;
const alertTimelineOpen = state => state.alertTimelineOpen || false;
const showAgentNames = state =>
  state.showAgentNames === undefined ? true : state.showAgentNames;
const hideOldAgents = state =>
  state.hideOldAgents === undefined ? true : state.hideOldAgents;
const showInactiveAgents = state => state.showInactiveAgents || false;
const showObservationClusters = state => state.showObservationClusters || false;
const observationClusterThreshold = state =>
  state.observationClusterThreshold || 500;
const showObservationHeatmap = state => state.showObservationHeatmap || false;
const showTrackHeatmap = state => state.showTrackHeatmap || false;
const showTrackFullStandard = state => state.showTrackFullStandard || false;
const showTrackFullColour = state => state.showTrackFullColour || false;
const observationHeatmapResolution = state =>
  state.observationHeatmapResolution || 25;
const trackHeatmapResolution = state => state.trackHeatmapResolution || 25;
// Opacity 0 (i.e. transparency 100%, fully see-through) is a legitimate,
// deliberately-chosen value - `|| 0.3` would treat it as falsy and silently
// snap back to the 0.3 default, so check the type instead.
const observationHeatmapOpacity = state =>
  typeof state.observationHeatmapOpacity === 'number'
    ? state.observationHeatmapOpacity
    : DEFAULT_OBSERVATION_HEATMAP_OPACITY;
const trackHeatmapOpacity = state =>
  typeof state.trackHeatmapOpacity === 'number'
    ? state.trackHeatmapOpacity
    : DEFAULT_TRACK_HEATMAP_OPACITY;
const trackColor = state => state.trackColor || 'yellow';
const showGrid = state => state.showGrid || false;
const showCoordinates = state => state.showCoordinates || false;

const ontologySelectors = {
  ontology,
  labels,
  languages,
  selectedLanguage,
  updateInterval,
  alertTimelineOpen,
  showAgentNames,
  hideOldAgents,
  showInactiveAgents,
  showObservationClusters,
  observationClusterThreshold,
  showObservationHeatmap,
  showTrackHeatmap,
  showTrackFullStandard,
  showTrackFullColour,
  observationHeatmapResolution,
  trackHeatmapResolution,
  observationHeatmapOpacity,
  trackHeatmapOpacity,
  trackColor,
  showGrid,
  showCoordinates
};

export default reducer;
export { ontologySelectors };
